import { LIMITS, TEAM_COLORS, TEAM_NAMES, type InputDeviceMode, type VehicleBlueprint } from "@neon-arena/shared";
import type { AudioSystem } from "../audio/AudioSystem.js";
import type { NetworkSnapshot, PlayerView } from "../networking/ClientNetwork.js";
import { formatLives } from "./livesFormat.js";
import { botMarker } from "./botTag.js";
import { buildEffectChips, renderEffectChipsHtml } from "./effectChips.js";

interface KillFeedEntry {
  text: string;
  ttl: number;
}

export class Hud {
  private matchEl = document.getElementById("match-status")!;
  private modeEl = document.getElementById("input-mode")!;
  private camModeEl = document.getElementById("camera-mode")!;
  private vehicleNameEl = document.getElementById("vehicle-name")!;
  private healthFillEl      = document.getElementById("health-fill")       as HTMLDivElement;
  private reloadFillEl      = document.getElementById("reload-fill")       as HTMLDivElement;
  private boostFillEl       = document.getElementById("boost-fill")        as HTMLDivElement;
  private dmgFrontFillEl    = document.getElementById("damage-front-fill") as HTMLDivElement;
  private dmgBodyFillEl     = document.getElementById("damage-body-fill")  as HTMLDivElement;
  private dmgRearFillEl     = document.getElementById("damage-rear-fill")  as HTMLDivElement;
  private damageZonesEl     = document.getElementById("damage-zones")      as HTMLDivElement;
  private scoreboardEl = document.getElementById("scoreboard")!;
  private msgEl = document.getElementById("message-overlay")!;
  private killFeedEl = document.getElementById("kill-feed")!;
  private winnerEl = document.getElementById("winner-overlay")!;
  private winnerTitleEl = document.getElementById("winner-title")!;
  private winnerCountdownEl = document.getElementById("winner-countdown")!;
  private winnerActionsTimer: ReturnType<typeof setTimeout> | null = null;
  private vehicleStatsEl = document.getElementById("vehicle-stats")!;
  private weaponStatusEl = document.getElementById("weapon-status")!;
  private weaponLabelEl  = document.getElementById("weapon-status-label")!;
  private weaponCountEl  = document.getElementById("weapon-status-count")!;
  private weapon2StatusEl = document.getElementById("weapon2-status");
  private weapon2LabelEl  = document.getElementById("weapon2-status-label");
  private weapon2CountEl  = document.getElementById("weapon2-status-count");
  private effectChipsEl  = document.getElementById("effect-chips");
  private prevEffectChipsHtml = "";
  private overlayTtl = 0;
  private eliminatedShown = false;
  private localReloadMs = 2500;

  // Kill feed
  private killFeed: KillFeedEntry[] = [];
  private prevScores = new Map<string, number>();
  private prevLives = new Map<string, number>();

  // Winner tracking
  private winnerShown = false;
  private winnerName = "";
  private prevEliminated = new Map<string, boolean>();
  private hadMultiplePlayers = false;

  // Scoreboard diff key — only rebuild innerHTML when content changes
  private prevScoreboardKey = "";

  bindAudio(audio: AudioSystem): void {
    const btn = document.getElementById("mute-btn") as HTMLButtonElement | null;
    if (!btn) return;
    const update = (): void => {
      btn.textContent = audio.muted ? "SFX OFF" : "SFX ON";
    };
    update();
    btn.addEventListener("click", () => {
      audio.playClick();
      audio.setMuted(!audio.muted);
      update();
    });
  }

  setStatus(s: string): void {
    this.matchEl.textContent = s.toUpperCase();
  }

  setInputMode(mode: InputDeviceMode): void {
    const label = mode === "gamepad" ? "CONTROLLER" : mode === "touch" ? "TOUCH" : "KEYBOARD";
    this.modeEl.textContent = `INPUT: ${label}`;
    document.body.classList.toggle("touch-mode", mode === "touch");
  }

  showGamepadConnection(connected: boolean, name: string): void {
    const txt = connected
      ? `CONTROLLER ONLINE — ${shortName(name)}`
      : "CONTROLLER OFFLINE";
    this.message(txt, 1.6);
  }

  setVehicleName(id: string, name: string): void {
    this.vehicleNameEl.textContent = `${id} — ${name.toUpperCase()}`;
  }

  setLocalReloadMs(ms: number): void {
    this.localReloadMs = ms;
  }

  setVehicleBlueprint(bp: VehicleBlueprint): void {
    const rows: Array<[string, number, { min: number; max: number }]> = [
      ["TOP SPEED", bp.handling.maxSpeed,     LIMITS.handling.maxSpeed],
      ["ACCEL",     bp.handling.acceleration, LIMITS.handling.acceleration],
      ["TRACTION",  bp.handling.traction,     LIMITS.handling.traction],
      ["ARMOUR",    bp.stats.armour,          LIMITS.stats.armour],
      ["MASS",      bp.stats.mass,            LIMITS.stats.mass],
    ];
    const color = bp.style.trimColor;
    this.vehicleStatsEl.innerHTML = rows.map(([label, val, lim]) => {
      const pct = Math.round(Math.max(0, Math.min(1, (val - lim.min) / (lim.max - lim.min))) * 100);
      const display = Number.isInteger(val) ? String(val) : val.toFixed(2);
      return `<div class="hud-stat-row">
        <span class="hud-stat-label">${label}</span>
        <span class="hud-stat-track"><span class="hud-stat-fill" style="width:${pct}%;background:${color}"></span></span>
        <span class="hud-stat-value">${display}</span>
      </div>`;
    }).join("");
    this.vehicleStatsEl.classList.remove("hidden");
  }

  setCameraMode(mode: "thirdPerson" | "firstPerson"): void {
    this.camModeEl.textContent = `CAM: ${mode === "firstPerson" ? "1P" : "3P"}`;
  }

  setCameraLabel(text: string): void {
    this.camModeEl.textContent = text;
  }

  message(text: string, seconds: number): void {
    if (this.eliminatedShown) return;
    this.msgEl.textContent = text;
    this.msgEl.classList.remove("hidden");
    this.overlayTtl = seconds;
  }

  showEliminated(): void {
    if (this.eliminatedShown) return;
    this.eliminatedShown = true;
    this.overlayTtl = Infinity;
    this.msgEl.textContent = "ELIMINATED — SPECTATING (TAB TO SWITCH)";
    this.msgEl.classList.remove("hidden");
    this.msgEl.classList.add("eliminated-overlay");
    const hw = document.getElementById("health-wrap");
    const rw = document.getElementById("reload-wrap");
    const bw = document.getElementById("boost-wrap");
    if (hw) hw.style.display = "none";
    if (rw) rw.style.display = "none";
    if (bw) bw.style.display = "none";
    if (this.effectChipsEl) { this.effectChipsEl.innerHTML = ""; this.prevEffectChipsHtml = ""; }
    this.damageZonesEl.style.display = "none";
  }

  setSpectateHint(name: string): void {
    if (!this.eliminatedShown) return;
    this.msgEl.textContent = `SPECTATING: ${name.toUpperCase()} — TAB TO SWITCH`;
  }

  clearEliminated(): void {
    this.eliminatedShown = false;
    this.overlayTtl = 0;
    this.msgEl.classList.add("hidden");
    this.msgEl.classList.remove("eliminated-overlay");
    const hw = document.getElementById("health-wrap");
    const rw = document.getElementById("reload-wrap");
    const bwc = document.getElementById("boost-wrap");
    if (hw) hw.style.display = "";
    if (rw) rw.style.display = "";
    if (bwc) bwc.style.display = "";
    this.damageZonesEl.style.display = "";
    this.winnerShown = false;
    this.winnerEl.classList.add("hidden");
    this.winnerCountdownEl.classList.add("hidden");
    this.winnerCountdownEl.textContent = "";
    if (this.winnerActionsTimer !== null) { clearTimeout(this.winnerActionsTimer); this.winnerActionsTimer = null; }
  }

  private scheduleWinnerActions(): void {
    if (this.winnerActionsTimer !== null) clearTimeout(this.winnerActionsTimer);
    let remaining = 3;
    const tick = (): void => {
      if (remaining <= 0) { window.location.reload(); return; }
      this.winnerCountdownEl.textContent = `RETURNING TO LOBBY IN ${remaining}…`;
      remaining--;
      this.winnerActionsTimer = setTimeout(tick, 1000);
    };
    this.winnerActionsTimer = setTimeout(() => {
      this.winnerCountdownEl.classList.remove("hidden");
      tick();
    }, 1500);
  }

  update(dt: number, snapshot: NetworkSnapshot): void {
    if (this.overlayTtl > 0 && this.overlayTtl !== Infinity) {
      this.overlayTtl -= dt;
      if (this.overlayTtl <= 0) this.msgEl.classList.add("hidden");
    }

    const me = snapshot.players.get(snapshot.ownId);
    if (me && !this.eliminatedShown) {
      const pct = Math.max(0, me.health) / Math.max(1, me.maxHealth);
      this.healthFillEl.style.width = `${pct * 100}%`;
      const scale = me.maxHealth > 0 ? 100 / me.maxHealth : 0;
      this.dmgFrontFillEl.style.width = `${Math.min(100, me.damageFront * scale)}%`;
      this.dmgBodyFillEl.style.width  = `${Math.min(100, me.damageBody  * scale)}%`;
      this.dmgRearFillEl.style.width  = `${Math.min(100, me.damageRear  * scale)}%`;
      const reloadPct = me.reloadRemaining > 0 ? me.reloadRemaining / this.localReloadMs : 0;
      this.reloadFillEl.style.width = `${(1 - Math.min(1, reloadPct)) * 100}%`;
      const boostPct = me.boostMax > 0 ? Math.max(0, me.boostEnergy) / me.boostMax : 0;
      this.boostFillEl.style.width = `${boostPct * 100}%`;

      // Weapon status indicator (bottom-right). Mirrors the server's primary
      // carry priority: homing > ricochet > shockwave > (mines idle display).
      this.weaponStatusEl.classList.remove("hidden", "mines", "homing", "wide", "ricochet", "shockwave");
      if (me.homingMissiles > 0) {
        this.weaponStatusEl.classList.add("homing");
        this.weaponLabelEl.textContent = "HOMING ROCKETS";
        this.weaponCountEl.textContent = `×${me.homingMissiles}`;
      } else if ((me.ricochetShots ?? 0) > 0) {
        this.weaponStatusEl.classList.add("ricochet");
        this.weaponLabelEl.textContent = "RICOCHET";
        this.weaponCountEl.textContent = `×${me.ricochetShots}`;
      } else if ((me.shockwaveCharges ?? 0) > 0) {
        this.weaponStatusEl.classList.add("shockwave");
        this.weaponLabelEl.textContent = "SHOCKWAVE";
        this.weaponCountEl.textContent = `×${me.shockwaveCharges}`;
      } else {
        const minePips = "◆".repeat(Math.max(0, me.mineCount)) + "◇".repeat(Math.max(0, 5 - me.mineCount));
        this.weaponStatusEl.classList.add("mines", ...(me.wideMineActive ? ["wide"] : []));
        this.weaponLabelEl.textContent = me.wideMineActive ? "MINES — WIDE MODE" : "MINES";
        this.weaponCountEl.textContent = minePips;
      }

      // Secondary slot (fire2): rocket ammo pips + cooldown/empty states.
      if (this.weapon2StatusEl && this.weapon2CountEl && this.weapon2LabelEl) {
        const ammo = me.rocketAmmo ?? 0;
        const cooling = (me.reloadRemaining2 ?? 0) > 0;
        this.weapon2StatusEl.classList.remove("hidden", "cooldown", "empty");
        if (ammo <= 0) this.weapon2StatusEl.classList.add("empty");
        else if (cooling) this.weapon2StatusEl.classList.add("cooldown");
        this.weapon2LabelEl.textContent = cooling && ammo > 0 ? "ROCKETS — LOADING" : "ROCKETS";
        this.weapon2CountEl.textContent = ammo > 0 ? "▲".repeat(Math.min(9, ammo)) : "EMPTY";
      }

      // Active power-up chips (overdrive / 5-way countdowns, armour pool)
      if (this.effectChipsEl) {
        const html = renderEffectChipsHtml(buildEffectChips(me, snapshot.serverTime));
        if (html !== this.prevEffectChipsHtml) {
          this.prevEffectChipsHtml = html;
          this.effectChipsEl.innerHTML = html;
        }
      }

      if (!me.alive && this.overlayTtl <= 0) {
        const remaining = me.nextRespawnAt > 0
          ? Math.max(0, Math.ceil((me.nextRespawnAt - snapshot.serverTime) / 1000))
          : 3;
        this.message(`WRECKED — RESPAWNING IN ${remaining}s`, 0.5);
      }
    }

    this.detectKills(snapshot);
    this.updateKillFeed(dt);
    this.checkWinner(snapshot);

    const sbKey = scoreboardKey(snapshot, this.winnerName);
    if (sbKey !== this.prevScoreboardKey) {
      this.prevScoreboardKey = sbKey;
      this.renderScoreboard(snapshot);
    }

    // Update prev state tracking
    snapshot.players.forEach((p) => {
      this.prevScores.set(p.id, p.score);
      this.prevLives.set(p.id, p.lives);
      this.prevEliminated.set(p.id, p.eliminated);
    });
    if (snapshot.players.size >= 2) this.hadMultiplePlayers = true;
  }

  private detectKills(snapshot: NetworkSnapshot): void {
    snapshot.players.forEach((p) => {
      const prevLives = this.prevLives.get(p.id) ?? p.lives;
      if (p.lives < prevLives) {
        // This player lost a life — find who got a kill (score increased)
        let killerName = "";
        snapshot.players.forEach((k) => {
          if (k.id === p.id) return;
          const prevScore = this.prevScores.get(k.id) ?? k.score;
          if (k.score > prevScore) killerName = k.name;
        });
        const isEliminated = p.eliminated && !(this.prevEliminated.get(p.id) ?? false);
        const suffix = isEliminated ? " [OUT]" : "";
        const text = killerName
          ? `${killerName.toUpperCase()} → ${p.name.toUpperCase()}${suffix}`
          : `${p.name.toUpperCase()} WRECKED${suffix}`;
        this.killFeed.unshift({ text, ttl: 3.5 });
        if (this.killFeed.length > 4) this.killFeed.length = 4;
      }
    });
  }

  private updateKillFeed(dt: number): void {
    this.killFeed.forEach((e) => { e.ttl -= dt; });
    this.killFeed = this.killFeed.filter((e) => e.ttl > 0);
    if (!this.killFeedEl) return;
    this.killFeedEl.innerHTML = this.killFeed
      .map((e) => {
        const opacity = Math.min(1, e.ttl / 0.5);
        return `<div class="kill-entry" style="opacity:${opacity}">${escapeHtml(e.text)}</div>`;
      })
      .join("");
  }

  private checkWinner(snapshot: NetworkSnapshot): void {
    // Detect round reset: if any previously eliminated player is no longer eliminated
    if (this.winnerShown) {
      let wasReset = false;
      snapshot.players.forEach((p) => {
        if ((this.prevEliminated.get(p.id) ?? false) && !p.eliminated) wasReset = true;
      });
      if (wasReset) {
        this.winnerShown = false;
        this.winnerName = "";
        this.winnerEl.classList.add("hidden");
        this.hadMultiplePlayers = snapshot.players.size >= 2;
      }
      return;
    }

    if (!this.hadMultiplePlayers) return;
    const players = [...snapshot.players.values()];
    if (players.length < 2) return;

    const active = players.filter((p) => !p.eliminated);
    if (active.length === 1) {
      this.winnerShown = true;
      this.winnerName = active[0].name;
      this.winnerTitleEl.textContent = `${active[0].name.toUpperCase()} WINS!`;
      this.winnerEl.classList.remove("hidden");
      this.scheduleWinnerActions();
    } else if (active.length === 0) {
      this.winnerShown = true;
      this.winnerName = "";
      this.winnerTitleEl.textContent = "DRAW — ROUND OVER";
      this.winnerEl.classList.remove("hidden");
      this.scheduleWinnerActions();
    }
  }

  private renderScoreboard(snapshot: NetworkSnapshot): void {
    const players: PlayerView[] = [...snapshot.players.values()];
    const teamsActive = players.some((p) => p.teamId > 0);

    const getStatus = (p: PlayerView): { text: string; cls: string } => {
      if (this.winnerShown && p.name === this.winnerName) return { text: "WIN", cls: "status-winner" };
      if (p.eliminated) return { text: "OUT", cls: "status-out" };
      if (!p.alive) return { text: "↺", cls: "status-respawn" };
      return { text: "●", cls: "status-alive" };
    };

    const playerRow = (p: PlayerView): string => {
      const isMe = p.id === snapshot.ownId;
      const cls = ["score-row", isMe ? "you" : "", p.eliminated ? "eliminated" : p.alive ? "" : "dead"]
        .filter(Boolean).join(" ");
      const livesHtml = p.eliminated
        ? `<span class="lives-out">OUT</span>`
        : `<span class="lives-pips">${formatLives(p.lives)}</span>`;
      const { text: statusText, cls: statusCls } = getStatus(p);
      const statusHtml = `<span class="score-status ${statusCls}">${statusText}</span>`;
      // Subtle bot disclosure: dimmed "·AI" suffix on bot rows (id-based —
      // arena bots carry human-like display names). Kill feed is untouched.
      const marker = botMarker(p.id);
      const nameHtml = marker
        ? `<span style="opacity:.75">${escapeHtml(p.name)} <span class="bot-tag" style="opacity:.55;font-size:.85em">${marker}</span></span>`
        : `<span>${escapeHtml(p.name)}</span>`;
      return `<div class="${cls}">${nameHtml}<span class="score-kills">${p.score}</span><span class="score-deaths">${p.deaths ?? 0}</span>${livesHtml}${statusHtml}</div>`;
    };

    const header = `<div class="score-row score-header"><span>PLAYER</span><span>K</span><span>D</span><span>LIVES</span><span>—</span></div>`;

    if (teamsActive) {
      const { 1: ts1, 2: ts2 } = snapshot.teamScores;
      const teamBanner = `<div class="team-score-banner">` +
        `<span style="color:${TEAM_COLORS[1]}">${TEAM_NAMES[1]} ${ts1}</span>` +
        ` <span class="team-score-sep">VS</span> ` +
        `<span style="color:${TEAM_COLORS[2]}">${TEAM_NAMES[2]} ${ts2}</span>` +
        `</div>`;

      const makeTeamSection = (teamId: number): string => {
        const teamPlayers = players.filter((p) => p.teamId === teamId);
        if (teamPlayers.length === 0) return "";
        teamPlayers.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
        const teamHeader = `<div class="score-row team-header" style="color:${TEAM_COLORS[teamId]}">${TEAM_NAMES[teamId]}</div>`;
        return teamHeader + teamPlayers.map(playerRow).join("");
      };

      const noTeam = players.filter((p) => p.teamId === 0);
      noTeam.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
      const noTeamRows = noTeam.length > 0
        ? `<div class="score-row team-header" style="color:${TEAM_COLORS[0]}">FFA</div>` + noTeam.map(playerRow).join("")
        : "";

      this.scoreboardEl.innerHTML = teamBanner + header + makeTeamSection(1) + makeTeamSection(2) + noTeamRows;
    } else {
      players.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
      this.scoreboardEl.innerHTML = header + players.map(playerRow).join("");
    }
  }
}

function scoreboardKey(snap: NetworkSnapshot, winnerName: string): string {
  const rows = [...snap.players.values()]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((p) => `${p.id}:${p.score}:${p.deaths}:${p.lives}:${p.eliminated}:${p.alive}:${p.teamId}`)
    .join("|");
  return `${rows}|${winnerName}|${snap.teamScores[1]}:${snap.teamScores[2]}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

function shortName(s: string): string {
  if (!s) return "PAD";
  const m = s.match(/^([^(]+)/);
  return (m ? m[1] : s).trim().toUpperCase().slice(0, 28);
}
